import { useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Sparkle, X, ArrowRight, CheckCircle, Storefront, FileText,
  UsersThree, ShieldCheck,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const SESSION_KEY = 'mm_profile_reminder_dismissed';
const FOREVER_KEY_PREFIX = 'mm_profile_reminder_never:';

/**
 * Friendly (dismissable) profile-completion reminder for EXISTING users whose
 * `onboarding_completed` flag has never been set (= created before the wizard
 * existed). Unlike <OnboardingWizard /> it does NOT block navigation — users
 * can dismiss it with "Más tarde" (persisted for the session) or go fix their
 * profile right away.
 */
export const ProfileCompletionReminder = ({ onDismiss }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hidingForever, setHidingForever] = useState(false);

  const issues = useMemo(() => {
    const arr = [];
    const role = (user?.role || 'cliente').toLowerCase();
    const fd = user?.fiscal_data || {};
    const hasRFC = !!(user?.rfc || fd.rfc);
    const hasRazon = !!(user?.fiscal_name || fd.razon_social);
    const hasRegime = !!user?.fiscal_regime;
    const hasSpecialties = Array.isArray(user?.specialties) && user.specialties.length > 0;
    const fiscalVerified = user?.fiscal_verification_status === 'verified';

    if (user?.onboarding_completed !== true) {
      arr.push({
        icon: UsersThree,
        text: 'Confirmar el rol con el que quieres operar (Cliente / Proveedor / Ambos).',
      });
    }
    if (role === 'proveedor' || role === 'ambos') {
      if (!hasSpecialties) {
        arr.push({ icon: Storefront, text: 'Selecciona las categorías de materiales que ofreces.' });
      }
      if (!hasRFC || !hasRazon || !hasRegime) {
        arr.push({ icon: FileText, text: 'Completa tus datos fiscales (RFC, razón social, régimen SAT).' });
      }
      if (!fiscalVerified) {
        arr.push({
          icon: ShieldCheck,
          text: 'Sube y valida tu Constancia de Situación Fiscal vigente (no mayor a 3 meses).',
        });
      }
    }
    if (arr.length === 0) {
      arr.push({
        icon: CheckCircle,
        text: 'Revisa que tu perfil tenga toda la información al día para una mejor experiencia.',
      });
    }
    return arr;
  }, [user]);

  const handleLater = () => {
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch (_) { /* ignore */ }
    if (onDismiss) onDismiss();
  };

  const handleDontAskAgain = async () => {
    setHidingForever(true);
    try {
      await api.post('/users/complete-onboarding', {
        role: user?.role || 'cliente',
        specialties: user?.specialties || [],
      });
      toast.success('Perfecto, no volveremos a mostrar este aviso.');
    } catch (_) {
      // non-fatal; silently dismiss for session anyway
    } finally {
      try {
        sessionStorage.setItem(SESSION_KEY, '1');
        if (user?.user_id || user?.email) {
          localStorage.setItem(
            `${FOREVER_KEY_PREFIX}${user.user_id || user.email}`,
            '1',
          );
        }
      } catch (_) { /* ignore */ }
      if (onDismiss) onDismiss();
    }
  };

  const handleGoToProfile = () => {
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (_) { /* ignore */ }
    if (onDismiss) onDismiss();
    navigate('/profile');
  };

  return (
    <div
      className="fixed inset-0 z-[9997] bg-zinc-950/75 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="profile-reminder-modal"
    >
      <div className="w-full max-w-lg bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl my-8 relative">
        <button
          type="button"
          onClick={handleLater}
          className="absolute top-3 right-3 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 p-1"
          aria-label="Cerrar"
          data-testid="profile-reminder-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 bg-orange-500 text-white flex items-center justify-center flex-shrink-0">
              <Sparkle className="w-6 h-6" weight="fill" />
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-widest text-orange-600 dark:text-orange-400 font-semibold">
                Hola{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
              </p>
              <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 mt-0.5">
                Completa tu perfil
              </h2>
            </div>
          </div>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-3 leading-relaxed">
            Notamos que aún te falta algo de información para aprovechar MaterMatch al máximo. Si eres
            proveedor, <strong>necesitas tu información fiscal y tu Constancia de Situación Fiscal</strong>
            {' '}para poder ver las cotizaciones disponibles.
          </p>
        </div>

        {/* Checklist */}
        <div className="px-6 pb-4">
          <div className="border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 p-4 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
              Lo que falta
            </p>
            {issues.map((issue, i) => {
              const Icon = issue.icon;
              return (
                <div key={i} className="flex items-start gap-3 text-sm text-zinc-700 dark:text-zinc-300">
                  <Icon className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" weight="bold" />
                  <span className="leading-snug">{issue.text}</span>
                </div>
              );
            })}
          </div>

          {/* Benefit strip */}
          <div className="mt-4 border-l-4 border-green-500 bg-green-50 dark:bg-green-950/30 px-4 py-3">
            <p className="text-sm text-green-900 dark:text-green-200 leading-snug">
              <span className="font-semibold">✓ Perfil completo =</span> acceso a cotizaciones disponibles
              en tus especialidades y mayor visibilidad con clientes.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border-t border-zinc-200 dark:border-zinc-800 flex flex-col gap-2">
          <Button
            type="button"
            onClick={handleGoToProfile}
            className="w-full rounded-none btn-primary h-11"
            data-testid="profile-reminder-goto-btn"
          >
            Completar mi perfil ahora <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleLater}
              className="flex-1 rounded-none text-zinc-600 dark:text-zinc-400 h-10"
              data-testid="profile-reminder-later-btn"
            >
              Más tarde
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={handleDontAskAgain}
              disabled={hidingForever}
              className="flex-1 rounded-none text-zinc-500 dark:text-zinc-500 h-10 text-xs"
              data-testid="profile-reminder-never-btn"
            >
              {hidingForever ? 'Guardando…' : 'No volver a mostrar'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Returns true if the reminder should show for this user right now. */
export const shouldShowProfileReminder = (user) => {
  if (!user) return false;
  if ((user.role || '').toLowerCase() === 'admin') return false;
  // New users (onboarding_completed === false) get the blocking wizard instead
  if (user.onboarding_completed === false) return false;
  // Already dismissed this session?
  try {
    if (sessionStorage.getItem(SESSION_KEY) === '1') return false;
    const foreverKey = `${FOREVER_KEY_PREFIX}${user.user_id || user.email || ''}`;
    if (foreverKey && localStorage.getItem(foreverKey) === '1') return false;
  } catch (_) { /* ignore */ }

  // Compute whether there's anything real to remind about. We show the reminder
  // whenever the profile is genuinely incomplete for the user's role, regardless
  // of the `onboarding_completed` flag. This makes the modal appear for the ~165
  // existing Atlas users whose flag is either missing OR was accidentally set
  // to `true` before they finished their fiscal setup.
  const role = (user.role || 'cliente').toLowerCase();
  const fd = user.fiscal_data || {};

  if (role === 'proveedor' || role === 'ambos') {
    const hasRFC = !!(user.rfc || fd.rfc);
    const hasRazon = !!(user.fiscal_name || fd.razon_social);
    const hasRegime = !!user.fiscal_regime;
    const hasSpecialties = Array.isArray(user.specialties) && user.specialties.length > 0;
    const fiscalVerified = user.fiscal_verification_status === 'verified';
    if (!hasRFC || !hasRazon || !hasRegime || !hasSpecialties || !fiscalVerified) {
      return true;
    }
  }

  // For clients (or "ambos" who already completed provider fields) we only
  // remind if they have literally never completed the onboarding wizard.
  if (user.onboarding_completed !== true) return true;

  return false;
};

export default ProfileCompletionReminder;
